import React, {
  memo, useState, useEffect, useCallback,
} from 'react';
import clsx from 'clsx';
import {
  Trash2, Check, Edit3,
} from 'react-feather';
import PropTypes from 'prop-types';
import './file-upload-success.scss';
import { FileUploadTypes, MAX_CHAR_COUNT } from '../../constants';
import FeaturedIcon from '../../../featured-icon/featured-icon';
import { IconColors, IconSizes } from '../../../icon';
import Input, { InputTypes } from '../../../input/input';
import Typography from '../../../typography';
import ProgressBar from '../../../progress-bar/progress-bar';
import Icon from '../../../icon/icon';
import Button, { ButtonSizes, ButtonTypes } from '../../../button/button';
import useTranslation from '../../../../../utils/translation-utils';
import { formatBytes } from '../../../../../utils/shape-utils';
import { getIconBasedOnType } from '../../utils';
import Tooltip from '../../../tooltip';

const FileUploadSuccess = ({
  type, file, progress, fileIndex, onDelete, autoUpload, updateFileName, openFileUploadWindow,
  allowFileNameEditing,
}) => {
  const [getTranslation] = useTranslation();
  const formattedFileSize = formatBytes(file.size);
  const [fileName, setFileName] = useState(file.name);
  const [editFileName, setEditFileName] = useState(false);
  const showMiniSection = type === FileUploadTypes.MINI;

  useEffect(() => {
    if (file.name !== fileName) {
      setFileName(file.name);
    }
  }, [file.name]);

  useEffect(() => {
    if (autoUpload && editFileName) {
      setFileName(file.name);
      setEditFileName(false);
    }
  }, [autoUpload]);

  const handleFileNameChange = useCallback((event) => {
    const { value } = event.target;

    if (value.length <= MAX_CHAR_COUNT) {
      setFileName(value);
    }
  }, []);

  const handleFileNameBlur = useCallback(() => {
    setFileName(fileName.trim());
  }, [fileName]);

  const handleSaveClick = useCallback(() => {
    updateFileName(fileIndex, fileName.trim());
    setEditFileName(false);
  }, [fileIndex, updateFileName, fileName]);

  const handleEditClick = useCallback(() => {
    setEditFileName(!editFileName);
  }, [editFileName]);

  const handleCancelClick = useCallback(() => {
    setFileName(file.name);
    setEditFileName(false);
  }, [file]);

  const handleFileNameClick = useCallback(() => {
    if (showMiniSection) {
      openFileUploadWindow();
    }
  }, [showMiniSection, openFileUploadWindow]);

  const renderTrashIcon = useCallback(({ size, color }) => (
    <Icon
      size={size}
      icon={Trash2}
      color={color}
    />
  ), []);

  const renderEditIcon = useCallback(({ size, color }) => (
    <Icon
      size={size}
      icon={Edit3}
      color={color}
    />
  ), []);

  const renderFeaturedIcon = useCallback(({ size, color }) => (
    <Icon
      size={size}
      color={color}
      icon={getIconBasedOnType(file.type)}
    />
  ), [file.type]);

  const getFileName = () => (
    <div className="fds-file-name-cont" onClick={handleFileNameClick}>
      <Typography
        className="fds-file-name"
        variant={Typography.Variants.sm}
        color={Typography.Colors.gray700}
        fontWeight={Typography.FontWeight.medium}
      >
        {file.name}
      </Typography>
    </div>
  );

  const getFileNameByType = () => (
    !showMiniSection ? getFileName() : (
      <Tooltip description={getTranslation('CLICK_TO_REUPLOAD_LABEL')}>
        {getFileName()}
      </Tooltip>
    )
  );

  const checkDeleteButtonDisabled = useCallback(() => {
    if (autoUpload) {
      return true;
    }
    return showMiniSection ? false : progress > 0;
  }, [progress, autoUpload, showMiniSection]);

  return (
    <div className={clsx('file-display-success', type)}>
      <div className="fds-description">
        <FeaturedIcon
          color={IconColors.PRIMARY}
          renderIcon={renderFeaturedIcon}
          size={showMiniSection ? IconSizes.X_SMALL : IconSizes.SMALL}
        />
        <div className="dsd-info">
          {editFileName ? (
            <Input
              autoFocus
              value={fileName}
              isInvalid={!fileName}
              type={InputTypes.TEXT}
              onBlur={handleFileNameBlur}
              onChange={handleFileNameChange}
              placeholder={getTranslation('ENTER_FILE_NAME')}
              suffixTooltip={fileName ? ''
                : getTranslation('MSG_VAL_TEXT_NOT_EMPTY', { label: getTranslation('FILE_NAME') })}
            />
          ) : getFileNameByType()}
          {!showMiniSection && (
            <Typography
              variant={Typography.Variants.sm}
              color={Typography.Colors.gray500}
              fontWeight={Typography.FontWeight.regular}
            >
              {`${formattedFileSize.size} ${formattedFileSize.unit}`}
            </Typography>
          )}
          {
            progress > 0 && !showMiniSection && (
              <div className="progress">
                <ProgressBar value={parseInt(progress, 10)} />
              </div>
            )
          }
        </div>
        {progress === 100 && !showMiniSection ? (
          <div className="success-icon">
            <Icon icon={Check} color={IconColors.LIGHT} size={IconSizes.MEDIUM} />
          </div>
        ) : (
          <div className="fds-actions">
            {!autoUpload && !showMiniSection && allowFileNameEditing && (
              <Button
                renderIcon={editFileName ? null : renderEditIcon}
                label={editFileName ? getTranslation('SAVE') : ''}
                onClick={editFileName ? handleSaveClick : handleEditClick}
                size={editFileName ? ButtonSizes.XXX_SMALL : ButtonSizes.X_SMALL}
                type={editFileName ? ButtonTypes.PRIMARY_OUTLINED : ButtonTypes.LINK}
                disabled={(editFileName && (!fileName || fileName === file.name)) || progress > 0}
              />
            )}
            <Button
              disabled={checkDeleteButtonDisabled()}
              renderIcon={editFileName ? null : renderTrashIcon}
              label={editFileName ? getTranslation('CANCEL') : ''}
              onClick={editFileName ? handleCancelClick : onDelete}
              size={editFileName ? ButtonSizes.XXX_SMALL : ButtonSizes.X_SMALL}
              type={editFileName ? ButtonTypes.DESTRUCTIVE_OUTLINED : ButtonTypes.LINK}
            />
          </div>
        )}
      </div>
    </div>
  );
};

FileUploadSuccess.defaultProps = {
  autoUpload: true,
  onDelete: () => {},
  updateFileName: () => {},
  allowFileNameEditing: true,
  openFileUploadWindow: () => {},
};

FileUploadSuccess.propTypes = {
  onDelete: PropTypes.func,
  autoUpload: PropTypes.bool,
  updateFileName: PropTypes.func,
  openFileUploadWindow: PropTypes.func,
  allowFileNameEditing: PropTypes.bool,
  progress: PropTypes.number.isRequired,
  fileIndex: PropTypes.number.isRequired,
  file: PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.number,
    type: PropTypes.string,
  }).isRequired,
  type: PropTypes.oneOf(Object.values(FileUploadTypes)).isRequired,
};

export default memo(FileUploadSuccess);
